import { InputText } from "primereact/inputtext"
import { Dropdown, DropdownChangeParams } from "primereact/dropdown"
import { Checkbox, CheckboxChangeParams } from "primereact/checkbox"
import commonStyles from "../../common.module.css"
import { useConstants } from "../../../api/queries/useConstants"
import { ConstantsResponseData } from "../../../api/types/constants"
import { OrderUnloading, UpdateOrderUnloadingPaylod, UpdateOrderUnloadingResponseData } from "../../../api/types/order"
import { Button } from "primereact/button"
import { useNavigate, useParams } from "react-router-dom"
import { useOrderDetail } from "../../../api/queries/useOrderDetail"
import { useUpdateUnloading } from "../../../api/queries/useUpdateUnloading"
import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react"
import FieldError, { FormErrors } from "../../FieldError"
import { OrderParams } from "."
import { classNames } from "primereact/utils"
import { validateUnloading } from "./validations/unloading"
import { validateEmail } from "./validations/common"
import { isOrderEditable } from "../StateTag"
import { AddressSimple } from "../../../api/types/address"
import AddressBook from "../../AddressBook"
import { usePrompt } from "../../../common/usePrompt"
import { PhoneInput } from "../../common/PhoneInput"
import { splitNumber } from "../../common/PhoneInput/utils"
import styles from "./styles.module.css"
import { scrollToFirstInvalidElement } from "../../../common/util"
import { countryItemTemplate, countryValueTemplate } from "../../common/country"

export default function OrderFormUnloading() {
  const { uuid } = useParams<OrderParams>()
  const navigate = useNavigate()
  const handleBackClick = () => navigate(`/orders/${uuid}/loading`)
  const { data: orderDetail } = useOrderDetail(uuid)
  const { data: constants }: { data: ConstantsResponseData | undefined } = useConstants()

  useEffect(() => {
    if (orderDetail && constants) {
      const { unloading } = orderDetail.results.orderDetail

      if (unloading) {
        setName(unloading.name)
        setStreet(unloading.street)
        setStreetDetails(unloading.streetDetails)
        setZip(unloading.zip)
        setCity(unloading.city)
        setCountry(unloading.country)
        setPerson(unloading.person)
        setEmail(unloading.email)
        setNotifyPhone(unloading.notifyPhone)
        setNotifySMS(unloading.notifySMS)
        setNotifyEmail(unloading.notifyEmail)
        setReference(unloading.reference)
        setNote(unloading.note)
        setTailgate(unloading.tailgate)
        setVatId(unloading.vatId)
        setIn(unloading.in)

        const countryPrefix = constants.results.countries
          .find((country) => country.value === unloading.country)?.prefix

        if (unloading.phone) {
          const { prefix, number } = splitNumber(constants.results.countries, unloading.phone)
          setPhonePrefix(prefix)
          setPhone(number)
        } else {
          if (countryPrefix) {
            setPhonePrefix(countryPrefix)
          }
        }

        if (unloading.mobile) {
          const { prefix, number } = splitNumber(constants.results.countries, unloading.mobile)
          setMobilePrefix(prefix)
          setMobile(number)
        } else {
          if (countryPrefix) {
            setMobilePrefix(countryPrefix)
          }
        }
      }

      setIsInited(true)
    }
  }, [orderDetail, constants])

  const updateUnloading = useUpdateUnloading(uuid)

  const [name, setName] = useState<string>("")
  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
    resetErrorsFor(["name"])
  }

  const [street, setStreet] = useState<string>("")
  const handleStreetChange = (event: ChangeEvent<HTMLInputElement>) => {
    setStreet(event.target.value)
    resetErrorsFor(["street"])
  }

  const [streetDetails, setStreetDetails] = useState<string | null>("")
  const handleStreetDetailsChange = (event: ChangeEvent<HTMLInputElement>) =>
    setStreetDetails(event.target.value)

  const [zip, setZip] = useState<string>("")
  const handleZIPChange = (event: ChangeEvent<HTMLInputElement>) => {
    setZip(event.target.value)
    resetErrorsFor(["zip"])
  }

  const [city, setCity] = useState<string>("")
  const handleCityChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCity(event.target.value)
    resetErrorsFor(["city"])
  }

  const [country, setCountry] = useState<string>("")
  const handleCountryChange = (event: DropdownChangeParams) => {
    setCountry(event.value)
    resetErrorsFor(["country"])

    const prefix = constants?.results.countries
      .find((country) => country.value === event.value)?.prefix

    if (prefix) {
      setPhonePrefix(prefix)
      setMobilePrefix(prefix)
    }
  }

  const [person, setPerson] = useState<string | null>("")
  const handlePersonChange = (event: ChangeEvent<HTMLInputElement>) => setPerson(event.target.value)

  const [phonePrefix, setPhonePrefix] = useState<string>("")
  const [phone, setPhone] = useState<number | null>(null)
  const handlePhoneChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setPhonePrefix(prefix)
    setPhone(value)
    resetErrorsFor(["phone", "mobile"])
  }

  const [mobilePrefix, setMobilePrefix] = useState<string>("")
  const [mobile, setMobile] = useState<number | null>(null)
  const handleMobileChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setMobilePrefix(prefix)
    setMobile(value)
    resetErrorsFor(["mobile", "phone"])
  }

  const [email, setEmail] = useState<string | null>("")
  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
    resetErrorsFor(["email"])
  }
  const validateEmailBlur = () => setErrors(Object.assign({}, errors, validateEmail(email)))

  const [notifyPhone, setNotifyPhone] = useState<boolean>(false)
  const handleNotifyPhoneChange = (event: CheckboxChangeParams) => setNotifyPhone(event.checked)

  const [notifySMS, setNotifySMS] = useState<boolean>(false)
  const handleNotifySMSChange = (event: CheckboxChangeParams) => setNotifySMS(event.checked)

  const [notifyEmail, setNotifyEmail] = useState<boolean>(false)
  const handleNotifyEmailChange = (event: CheckboxChangeParams) => setNotifyEmail(event.checked)

  const [reference, setReference] = useState<string | null>("")
  const handleReferenceChange = (event: ChangeEvent<HTMLInputElement>) =>
    setReference(event.target.value)

  const [note, setNote] = useState<string | null>("")
  const handleNoteChange = (event: ChangeEvent<HTMLInputElement>) => setNote(event.target.value)

  const [tailgate, setTailgate] = useState<boolean>(false)
  const handleTailgateChange = (event: CheckboxChangeParams) => setTailgate(event.checked)

  const [vatId, setVatId] = useState<string | null>(null)
  const [_in, setIn] = useState<string | null>(null)

  const [addressBookUuid, setAddressBookUuid] = useState<string>("")

  const [errors, setErrors] = useState<FormErrors<OrderUnloading>>()

  const handleFormSubmit = async (event: SyntheticEvent) => {
    event.preventDefault()

    const payload: UpdateOrderUnloadingPaylod = {
      unloading: {
        name,
        street,
        streetDetails,
        zip,
        city,
        country,
        person,
        phone: phone ? `${phonePrefix}${phone}` : "",
        mobile: mobile ? `${mobilePrefix}${mobile}` : "",
        email,
        notifyPhone,
        notifySMS,
        notifyEmail,
        reference,
        note,
        tailgate,
        vatId,
        in: _in,
        addressBookUuid: addressBookUuid || null,
      }
    }

    const [isValid, errors] = validateUnloading(payload)
    setErrors(errors)

    if (isValid) {
      await updateUnloading.mutateAsync(payload)
        .then(() => {
          setChangeCounter(0)
          setNextStep(true)
        })
        .catch(({ response }) => {
          let { data }: { data: UpdateOrderUnloadingResponseData } = response
          let respErrors: FormErrors<OrderUnloading> = {}

          data.errors.forEach((error) => {
            if (typeof error.property === "string") {
              respErrors[error.property] = error.message
            }
          })

          setErrors(respErrors)
          scrollToFirstInvalidElement()
        })
    }
  }

  const [isAddressBookVisible, setIsAddressBookVisible] = useState<boolean>(false)
  const onAddressBookShow = () => setIsAddressBookVisible(true)
  const onAddressBookHide = () => setIsAddressBookVisible(false)
  const onAddressBookSelect = (address: AddressSimple) => {
    setAddressBookUuid(address.uuid)

    setName(address.name)
    setStreet(address.street)
    setStreetDetails(address.streetDetails)
    setZip(address.zip)
    setCity(address.city)
    setCountry(address.country)
    setPerson(address.person)
    setEmail(address.email)
    setVatId(address.vatId)
    setIn(address.in)
    setNote(address.note)

    if (!tailgate) {
      setTailgate(address.tailgate)
    }

    if (!notifyPhone) {
      setNotifyPhone(address.notifyPhone)
    }

    if (!notifySMS) {
      setNotifySMS(address.notifySMS)
    }

    if (!notifyEmail) {
      setNotifyEmail(address.notifyEmail)
    }

    if (address.phone) {
      const { prefix, number } = splitNumber(constants?.results.countries || [], address.phone)
      setPhonePrefix(prefix)
      setPhone(number)
    } else {
      setPhonePrefix("")
      setPhone(null)
    }

    if (address.mobile) {
      const { prefix, number } = splitNumber(constants?.results.countries || [], address.mobile)
      setMobilePrefix(prefix)
      setMobile(number)
    } else {
      setMobilePrefix("")
      setMobile(null)
    }

    setIsAddressBookVisible(false)
    resetErrorsFor(["name", "street", "zip", "city", "country", "phone", "mobile"])
  }

  const [isInited, setIsInited] = useState<boolean>(false)
  const [changeCounter, setChangeCounter] = useState<number>(0)
  const [nextStep, setNextStep] = useState<boolean>(false)

  // prompt about unsaved form changes
  useEffect(() => {
    if (isInited) {
      setChangeCounter(i => i + 1)
    }
  }, [isInited, name, street, streetDetails, zip, city, country, person, phone, mobile, email,
    notifyPhone, notifySMS, notifyEmail, reference, note, tailgate])

  usePrompt("Máte neuložené změny. Opravdu chcete odejít?", changeCounter > 1)

  // redirect to next step
  useEffect(() => {
    if (changeCounter <= 1 && nextStep) {
      navigate(`/orders/${uuid}/goods`)
    }
  }, [changeCounter, nextStep, navigate, uuid])

  const isEditable = isOrderEditable(orderDetail?.results.orderDetail.state || 0)

  const resetErrorsFor = (names: string[]) => {
    let obj: { [key: string]: string } = {}

    names.forEach((name) => { obj[name] = "" })

    setErrors(Object.assign({}, errors, obj))
  }

  return (
    <>
      <form className={`col-12 ${isEditable ? "" : "p-disabled"}`} onSubmit={handleFormSubmit}>
        <div className={"p-fluid"}>
          <div className={"border-round-top flex block-title block-title_blue align-items-center px-3"}>
            Místo vykládky

            <Button type={"button"} label={"Adresář"} icon={"pi pi-book"} title={"Adresář"}
              className={"p-button-icon-only p-button-text ml-auto text-color"}
              onClick={onAddressBookShow} />
          </div>

          <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
            <div className={"grid"}>
              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={name} onChange={handleNameChange}
                    className={classNames({ "p-invalid": !!errors?.name })}
                    aria-autocomplete={"none"} />
                  <label className={commonStyles.labelRequired}>Název příjemce:</label>
                </span>
                <FieldError error={errors?.name} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={street} onChange={handleStreetChange}
                    className={classNames({ "p-invalid": !!errors?.street })}
                    aria-autocomplete={"none"} />
                  <label className={commonStyles.labelRequired}>Ulice a číslo domu:</label>
                </span>
                <FieldError error={errors?.street} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={streetDetails || ""} onChange={handleStreetDetailsChange}
                    aria-autocomplete={"none"}
                    className={classNames({ "p-invalid": !!errors?.streetDetails })} />
                  <label>Upřesnění:</label>
                </span>
                <FieldError error={errors?.streetDetails} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={zip} onChange={handleZIPChange}
                    className={classNames({ "p-invalid": !!errors?.zip })}
                    aria-autocomplete={"none"} />
                  <label className={commonStyles.labelRequired}>PSČ:</label>
                </span>
                <FieldError error={errors?.zip} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={city} onChange={handleCityChange}
                    className={classNames({ "p-invalid": !!errors?.city })}
                    aria-autocomplete={"none"} />
                  <label className={commonStyles.labelRequired}>Obec:</label>
                </span>
                <FieldError error={errors?.city} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className="p-float-label">
                  <Dropdown options={constants?.results.countries || []}
                    value={country} onChange={handleCountryChange}
                    className={classNames({ "p-invalid": !!errors?.country })}
                    itemTemplate={countryItemTemplate} valueTemplate={countryValueTemplate} />
                  <label className={commonStyles.labelRequired}>Stát:</label>
                </span>
                <FieldError error={errors?.country} />
              </div>
            </div>
          </div>

          <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>
            Kontaktní osoba
          </div>

          <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
            <div className={"grid"}>
              <div className={"field col-12 md:col-8"}>
                <label className={"mb-3"}>Avizování vykládky:</label>
                <div className={"formgroup-inline"}>
                  <div className={"field-checkbox"}>
                    <Checkbox name={"phone"} inputId={"phone"}
                      checked={notifyPhone} onChange={handleNotifyPhoneChange}
                      className={classNames({ "p-invalid": !!errors?.notifyPhone })} />
                    <label htmlFor={"phone"}>Telefonicky</label>
                  </div>
                  <div className={"field-checkbox"}>
                    <Checkbox name={"sms"} inputId={"sms"}
                      checked={notifySMS} onChange={handleNotifySMSChange}
                      className={classNames({ "p-invalid": !!errors?.notifySMS })} />
                    <label htmlFor={"sms"}>Pomocí SMS</label>
                  </div>
                  <div className={"field-checkbox"}>
                    <Checkbox name={"email"} inputId={"email"}
                      checked={notifyEmail} onChange={handleNotifyEmailChange}
                      className={classNames({ "p-invalid": !!errors?.notifyEmail })} />
                    <label htmlFor={"email"}>E-mailem</label>
                  </div>
                </div>
                <FieldError error={errors?.notifyPhone} />
                <FieldError error={errors?.notifySMS} />
                <FieldError error={errors?.notifyEmail} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={person || ""} onChange={handlePersonChange}
                    aria-autocomplete={"none"}
                    className={classNames({ "p-invalid": !!errors?.person })} />
                  <label>Jméno:</label>
                </span>
                <FieldError error={errors?.person} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <PhoneInput placeholder={"Mobil:"} prefix={mobilePrefix} value={mobile} onChange={handleMobileChange}
                  isRequired={(notifyPhone && !phone) || notifySMS} error={errors?.mobile} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <PhoneInput placeholder={"Telefon:"} prefix={phonePrefix} value={phone} onChange={handlePhoneChange}
                  isRequired={notifyPhone && !mobile} error={errors?.phone} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={email || ""} onChange={handleEmailChange} onBlur={validateEmailBlur}
                    className={classNames({ "p-invalid": !!errors?.email })}
                    aria-autocomplete={"none"} />
                  <label className={notifyEmail ? commonStyles.labelRequired : ""}>E-mail:</label>
                </span>
                <FieldError error={errors?.email} />
              </div>
            </div>
          </div>

          <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>
            Důležité informace k vykládce
          </div>

          <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
            <div className={"grid"}>
              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText maxLength={30} value={reference || ""} onChange={handleReferenceChange}
                    className={classNames({ "p-invalid": !!errors?.reference })} />
                  <label>Reference k vykládce:</label>
                </span>
                <FieldError error={errors?.reference} />
              </div>

              <div className={"field col-6 md:col-4"}>
                <div className={"field-checkbox"}>
                  <Checkbox name={"tailgate"} inputId={"tailgate"}
                    checked={tailgate} onChange={handleTailgateChange}
                    className={classNames({ "p-invalid": !!errors?.tailgate })} />
                  <label htmlFor={"tailgate"}>Auto s čelem</label>
                </div>
                <FieldError error={errors?.tailgate} />
              </div>

              <div className={"field col-12"}>
                <span className={"p-float-label"}>
                  <InputText value={note || ""} maxLength={50} onChange={handleNoteChange}
                    className={classNames({ "p-invalid": !!errors?.note })} />
                  <label>Dispozice k vykládce:</label>
                </span>
                <FieldError error={errors?.note} />
              </div>
            </div>
          </div>
        </div>

        {isEditable && (
          <div className={`${styles.controls} md:flex`}>
            <div className={"flex-1"}>
              <Button type={"button"} label={"Předchozí krok"}
                className={"p-button p-button-sm p-button-secondary mr-1"}
                icon={"pi pi-arrow-circle-left"}
                disabled={updateUnloading.isLoading} onClick={handleBackClick} />
            </div>

            <div className={"flex-1 text-right"}>
              <Button type={"submit"} label={"Další krok"}
                className={"p-button p-button-sm p-button-success"}
                icon={"pi pi-arrow-circle-right"} iconPos={"right"}
                disabled={updateUnloading.isLoading} />
            </div>
          </div>
        )}
      </form>

      <AddressBook isVisible={isAddressBookVisible} onHide={onAddressBookHide}
        onSelect={onAddressBookSelect} />
    </>
  )
}
